import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, RefreshCw, Fingerprint, Laptop } from 'lucide-react';

export default function VerifyOtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [trustDevice, setTrustDevice] = useState(true); // Default to true for better UX
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);

  const userId = location.state?.userId;
  const devOtpCode = location.state?.otpCode;
  const rememberMe = location.state?.rememberMe;

  useEffect(() => {
    if (!userId) {
      navigate('/login', { replace: true });
    }
    checkBiometricAvailability();
  }, [userId, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const checkBiometricAvailability = async () => {
    if (window.PublicKeyCredential) {
      try {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setBiometricAvailable(available);
      } catch (e) {
        setBiometricAvailable(false);
      }
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      toast.error('Inserisci il codice a 6 cifre');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.verifyOtp({ 
        user_id: userId, 
        otp_code: otp,
        trust_device: trustDevice,
        device_name: navigator.userAgent,
        remember_me: rememberMe
      });
      const { token, user, device_token } = res.data;
      
      // Save device token if trusted
      if (device_token) {
        localStorage.setItem('trusted_device_token', device_token);
      }
      
      // Check if we should offer biometric setup
      const biometricRegistered = localStorage.getItem('biometric_credential');
      if (biometricAvailable && !biometricRegistered && rememberMe) {
        setPendingLoginData({ token, user });
        setShowBiometricSetup(true);
      } else {
        completeLogin(token, user);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Codice OTP non valido');
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = (token, user) => {
    login(user, token);
    toast.success('Accesso effettuato!');
    navigate('/dashboard', { replace: true, state: { user } });
  };

  const handleSetupBiometric = async () => {
    try {
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const credential = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: {
            name: 'OBC Cloud',
            id: window.location.hostname
          },
          user: {
            id: Uint8Array.from(pendingLoginData.user.user_id, c => c.charCodeAt(0)),
            name: pendingLoginData.user.email,
            displayName: pendingLoginData.user.name || pendingLoginData.user.email
          },
          pubKeyCredParams: [
            { type: 'public-key', alg: -7 },
            { type: 'public-key', alg: -257 }
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required'
          },
          timeout: 60000
        }
      });

      if (credential) {
        // Save credential ID for future logins
        const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
        localStorage.setItem('biometric_credential', credentialId);
        localStorage.setItem('biometric_email', pendingLoginData.user.email);
        toast.success('Impronta digitale registrata!');
      }
    } catch (err) {
      if (err.name !== 'NotAllowedError') {
        console.error('Biometric setup error:', err);
      }
    }
    
    // Complete login regardless of biometric setup result
    completeLogin(pendingLoginData.token, pendingLoginData.user);
  };

  const handleSkipBiometric = () => {
    completeLogin(pendingLoginData.token, pendingLoginData.user);
  };

  const handleResend = async () => {
    try {
      const res = await authAPI.resendOtp(userId);
      setCountdown(60);
      toast.success('Nuovo codice OTP inviato');
      if (res.data.otp_code) {
        toast.info(`Dev OTP: ${res.data.otp_code}`, { duration: 10000 });
      }
    } catch (err) {
      toast.error('Errore nel reinvio del codice');
    }
  };

  // Biometric setup screen
  if (showBiometricSetup) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md border-border bg-card animate-fade-in">
          <CardHeader className="text-center">
            <div className="mx-auto w-14 h-14 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center mb-4">
              <Fingerprint className="w-7 h-7 text-primary" />
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
              Accesso Rapido
            </CardTitle>
            <CardDescription>
              Vuoi abilitare l'accesso con impronta digitale per i prossimi login?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Potrai accedere piu' velocemente usando l'impronta digitale del tuo dispositivo.
            </p>
            
            <Button
              onClick={handleSetupBiometric}
              data-testid="setup-biometric-btn"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Fingerprint className="w-4 h-4 mr-2" />
              Abilita Impronta Digitale
            </Button>
            
            <Button
              variant="ghost"
              onClick={handleSkipBiometric}
              data-testid="skip-biometric-btn"
              className="w-full text-muted-foreground"
            >
              Salta per ora
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-border bg-card animate-fade-in">
        <CardHeader className="text-center">
          <div className="mx-auto w-14 h-14 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center mb-4">
            <Shield className="w-7 h-7 text-primary" />
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
            Verifica 2FA
          </CardTitle>
          <CardDescription>
            Inserisci il codice a 6 cifre inviato alla tua email
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {devOtpCode && (
            <div className="p-3 rounded-md bg-primary/10 border border-primary/20 text-center">
              <p className="text-xs text-muted-foreground mb-1">Codice OTP (dev mode)</p>
              <p className="text-2xl font-mono font-bold tracking-widest text-primary" data-testid="dev-otp-code">
                {devOtpCode}
              </p>
            </div>
          )}

          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={otp}
              onChange={setOtp}
              data-testid="otp-input"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <div className="flex items-center space-x-2 justify-center">
            <Checkbox
              id="trustDevice"
              checked={trustDevice}
              onCheckedChange={setTrustDevice}
              data-testid="trust-device-checkbox"
            />
            <Label 
              htmlFor="trustDevice" 
              className="text-sm text-muted-foreground cursor-pointer flex items-center"
            >
              <Laptop className="w-4 h-4 mr-1" />
              Ricorda questo dispositivo per 30 giorni
            </Label>
          </div>

          <Button
            onClick={handleVerify}
            data-testid="verify-otp-btn"
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            disabled={loading || otp.length !== 6}
          >
            {loading ? 'Verifica...' : 'Verifica Codice'}
          </Button>

          <div className="text-center">
            <Button
              variant="ghost"
              data-testid="resend-otp-btn"
              disabled={countdown > 0}
              onClick={handleResend}
              className="text-muted-foreground hover:text-foreground"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {countdown > 0 ? `Reinvia tra ${countdown}s` : 'Reinvia codice'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
